// Обновление сессии (accessToken/refreshToken) — ЕДИНАЯ точка для всего веба,
// 1:1 с логикой RN (callWithRefreshTokenIfNeeded / saga refreshToken):
//   GET /auth/refreshToken?refreshToken&idProfile&fingerprint → новая сессия,
//   персистим в localStorage; при невалидном refresh — разлогин.
//
// Refresh-токен одноразовый, поэтому важно, чтобы в момент времени шёл ОДИН
// refresh:
//  - single-flight внутри вкладки (общий промис для interceptor'а axios и
//    tokenCheckerMiddleware — раньше у них были РАЗНЫЕ флаги, и они могли
//    отправить один и тот же refresh-токен дважды → второй ловил 403 → разлогин);
//  - между вкладками — Web Locks API (navigator.locks): вторая вкладка ждёт
//    первую и, увидев, что токен уже обновлён, просто берёт новый из хранилища.
import axios from "axios";

const apiKey = process.env.REACT_APP_API_KEY;
const LOCK_NAME = "velvet-session-refresh";

let inflight: Promise<string | null> | null = null;

type RefreshFailure = "invalid" | "network";
let lastFailure: RefreshFailure | null = null;
export const getLastRefreshFailure = () => lastFailure;

const ls = () => window.localStorage;

export const clearSession = () => {
  ls().removeItem("accessToken");
  ls().removeItem("refreshToken");
  ls().removeItem("idProfile");
  ls().removeItem("tokenTime");
};

// Разлогин — ТОЛЬКО когда сам refresh-токен невалиден (как в RN: signOut на
// провал refresh). Чистим сессию и перезагружаем — приложение стартует
// неавторизованным и уводит на вход.
let signingOut = false;
const isDevAutologin = () =>
  process.env.REACT_APP_ENV_MODE === "development" &&
  process.env.REACT_APP_DEV_AUTOLOGIN !== "0" &&
  !(window as any).__DEV_NO_AUTOLOGIN__ &&
  !!process.env.REACT_APP_DEV_ACCESS_TOKEN;
export const forceSignOut = (reason: string) => {
  // DEV-autologin: сессию держит devSeed (вшитые токены) — не чистим и не
  // перезагружаем, иначе бесконечный цикл reload → 401 → reload.
  if (isDevAutologin()) {
    console.warn("[session] (dev autologin) would sign out:", reason);
    return;
  }
  if (signingOut) return;
  signingOut = true;
  console.error("[session] sign out:", reason);
  clearSession();
  window.location.reload();
};

const doRefresh = async (usedRefreshToken: string, from: string): Promise<string | null> => {
  // Пока ждали лок, другая вкладка уже могла обновить сессию — берём её.
  const current = ls().refreshToken;
  if (current && current !== usedRefreshToken && ls().accessToken) {
    lastFailure = null;
    return ls().accessToken as string;
  }
  const idProfile = ls().idProfile;
  const fingerPrint = ls().fingerPrint;
  if (!current || !idProfile || !fingerPrint) {
    lastFailure = "invalid";
    return null;
  }
  try {
    const resp = await axios.get(
      `/auth/refreshToken?refreshToken=${encodeURIComponent(current)}&idProfile=${encodeURIComponent(idProfile)}` +
        `&fingerprint=${encodeURIComponent(fingerPrint)}&appsFlyerId=&from=${encodeURIComponent(from)}&_=${Date.now()}`,
      {
        skipAuth: true,
        headers: {
          ...(apiKey ? { "X-API-KEY": apiKey } : {}),
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
        },
      } as any
    );
    const data = resp.data || {};
    if (!data.accessToken) throw new Error("refresh response without accessToken");
    ls().accessToken = data.accessToken;
    ls().refreshToken = data.refreshToken || current;
    if (data.idProfile) ls().idProfile = data.idProfile;
    ls().tokenTime = new Date().toISOString();
    lastFailure = null;
    return data.accessToken as string;
  } catch (e: any) {
    // Другая вкладка успела обновить, пока шёл наш запрос (наш токен уже
    // использован) — это не повод разлогинивать.
    const after = ls().refreshToken;
    if (after && after !== current && ls().accessToken) {
      lastFailure = null;
      return ls().accessToken as string;
    }
    const status = e?.response?.status;
    const data = e?.response?.data;
    const invalid =
      status === 401 || status === 403 || status === 304 ||
      data?.errorCode === 17 ||
      (typeof data?.errorMessage === "string" && data.errorMessage.includes("Not Found Refresh Token"));
    lastFailure = invalid ? "invalid" : "network";
    console.error("[session] refresh failed:", status, data ?? e?.message);
    return null;
  }
};

/**
 * Обновить сессию. Возвращает новый accessToken или null.
 * Параллельные вызовы (и из разных вкладок) делят один refresh.
 * При невалидном refresh-токене — разлогин (если signOutOnInvalid).
 */
export const refreshSession = (from = "web", signOutOnInvalid = true): Promise<string | null> => {
  if (inflight) return inflight;
  const used = ls().refreshToken as string;
  const run = async () => {
    const locks = (navigator as any).locks;
    const token = locks && locks.request
      ? await locks.request(LOCK_NAME, () => doRefresh(used, from))
      : await doRefresh(used, from);
    if (!token && lastFailure === "invalid" && signOutOnInvalid) {
      forceSignOut(`refresh token invalid (${from})`);
    }
    return token as string | null;
  };
  inflight = run().finally(() => { inflight = null; });
  return inflight;
};

export const isRefreshInFlight = () => !!inflight;
